import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { LEARN, stickerButton } from "../theme";
import { GLYPH_STROKES } from "../../../data/glyphStrokes";
import {
  GUIDE_LINES,
  crayonWidthFactor,
  glyphBoxFor,
  judgeAttempt,
  mixHex,
  pointAlong,
  sampleGlyphStrokes,
} from "./glyphGeometry";

/**
 * GlyphTracer — the one canvas letter/numeral tracer for Pixley Learn.
 *
 * Paper card with kindergarten guide lines, the whole glyph faint, the current
 * stroke highlighted with a numbered start dot and a star at the end, a demo
 * pencil that shows the stroke before the child tries (and after a miss), and
 * crayon ink that turns solid with a sparkle burst when a stroke passes.
 *
 * Strokes come from GLYPH_STROKES[glyph] unless `strokes` is passed
 * ([{ d, start, prompt }] in the same 1×1 convention).
 */
export function describeGlyph(glyph) {
  const g = String(glyph || "");
  if (/^[0-9]$/.test(g)) return `the number ${g}`;
  if (/^[A-Z]$/.test(g)) return `big ${g}`;
  if (/^[a-z]$/.test(g)) return `little ${g}`;
  return g;
}

const SPARKLE_COUNT = 16;

function nowMs() {
  return typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
}

function drawStar(ctx, cx, cy, r, fill, stroke) {
  ctx.beginPath();
  for (let i = 0; i < 10; i += 1) {
    const rad = i % 2 === 0 ? r : r * 0.48;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const x = cx + Math.cos(a) * rad;
    const y = cy + Math.sin(a) * rad;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  if (stroke) {
    ctx.lineWidth = Math.max(2, r * 0.3);
    ctx.lineJoin = "round";
    ctx.strokeStyle = stroke;
    ctx.stroke();
  }
  ctx.fillStyle = fill;
  ctx.fill();
}

function strokePolyline(ctx, poly, box, color, width, from = 0, to = 1) {
  if (!poly || poly.length < 2) return;
  const total = poly.length - 1;
  const a = Math.max(0, Math.floor(from * total));
  const b = Math.min(total, Math.ceil(to * total));
  if (b <= a) return;
  ctx.beginPath();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.moveTo(box.x + poly[a].x * box.s, box.y + poly[a].y * box.s);
  for (let i = a + 1; i <= b; i += 1) ctx.lineTo(box.x + poly[i].x * box.s, box.y + poly[i].y * box.s);
  ctx.stroke();
}

function strokeInk(ctx, ink, box, color, baseWidth, alpha = 1) {
  if (!ink || !ink.length) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  if (ink.length === 1) {
    ctx.beginPath();
    ctx.arc(box.x + ink[0].x * box.s, box.y + ink[0].y * box.s, (baseWidth * ink[0].w) / 2, 0, Math.PI * 2);
    ctx.fill();
  }
  for (let i = 1; i < ink.length; i += 1) {
    const p = ink[i - 1];
    const q = ink[i];
    ctx.beginPath();
    ctx.lineWidth = baseWidth * q.w;
    ctx.moveTo(box.x + p.x * box.s, box.y + p.y * box.s);
    ctx.lineTo(box.x + q.x * box.s, box.y + q.y * box.s);
    ctx.stroke();
  }
  ctx.restore();
}

function ShowMeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 17c4-9 8-9 12-1" />
      <path d="M13 13l4 4 4-4" />
    </svg>
  );
}

function RestartIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 12a8 8 0 1 0 3-6.2" />
      <path d="M4 4v5h5" />
    </svg>
  );
}

export default function GlyphTracer({
  glyph,
  strokes: strokesProp,
  accent = LEARN.color.blue,
  accentDeep = LEARN.color.blueDeep,
  label,
  onStrokeDone,
  onComplete,
  onWrong,
  sfx,
  speak,
  reduceMotion = false,
  showGuide = true,
  style,
}) {
  const strokeDefs = useMemo(
    () => (strokesProp && strokesProp.length ? strokesProp : GLYPH_STROKES[glyph]?.strokes || []),
    [glyph, strokesProp],
  );
  const strokes = useMemo(() => sampleGlyphStrokes(strokeDefs), [strokeDefs]);

  const paperRef = useRef(null);
  const canvasRef = useRef(null);

  // Dev-only hook so headless QA can drive the tracer with synthetic pointers.
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    const c = canvasRef.current;
    if (c) c.__learnTrace = { strokes, glyphBoxFor };
  }, [strokes]);
  const ctxRef = useRef(null);
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 });
  const rafRef = useRef(0);
  const timers = useRef(new Set());
  const doneRef = useRef(false);
  const cbRef = useRef({});
  cbRef.current = { onStrokeDone, onComplete, onWrong, sfx, speak };

  // Imperative model: everything the canvas needs each frame lives here.
  const m = useRef({
    strokes: [],
    index: 0,
    phase: "trace", // trace | pass | done
    drawing: null, // { pointerId }
    ink: [],
    widthFactor: 1,
    lastPx: null,
    passedInks: [],
    demo: null, // { t0, dur, static }
    fail: null, // { t0, ink }
    pass: null, // { t0 }
    bounce: null, // { t0 }
    sparkles: [],
    penSeen: 0,
  });

  const [strokeIndex, setStrokeIndex] = useState(0);
  const [phase, setPhase] = useState("trace");

  const total = strokes.length;
  const current = strokes[Math.min(strokeIndex, Math.max(0, total - 1))];

  const after = useCallback((ms, fn) => {
    const id = setTimeout(() => {
      timers.current.delete(id);
      fn();
    }, ms);
    timers.current.add(id);
    return id;
  }, []);

  const animating = useCallback(() => {
    const s = m.current;
    return !reduceMotion || !!s.demo || !!s.fail || !!s.pass || !!s.bounce || s.sparkles.length > 0 || !!s.drawing;
  }, [reduceMotion]);

  // ---------- drawing ----------
  const draw = useCallback(
    (now) => {
      const ctx = ctxRef.current;
      const { w, h } = sizeRef.current;
      if (!ctx || !w || !h) return;
      const s = m.current;
      const box = glyphBoxFor(w, h);
      ctx.clearRect(0, 0, w, h);
      ctx.save();

      // Soft shake after a miss.
      if (s.fail && !reduceMotion) {
        const t = now - s.fail.t0;
        if (t < 420) ctx.translate(Math.sin(t / 34) * 7 * (1 - t / 420), 0);
      }
      // Bounce once the glyph is finished.
      if (s.bounce && !reduceMotion) {
        const t = Math.min(1, (now - s.bounce.t0) / 560);
        const k = 1 + Math.sin(t * Math.PI) * 0.07;
        const cx = box.x + box.s / 2;
        const cy = box.y + box.s / 2;
        ctx.translate(cx, cy);
        ctx.scale(k, k);
        ctx.translate(-cx, -cy);
      }

      // Kindergarten guide lines: top, dashed midline, baseline.
      const inset = Math.max(10, w * 0.04);
      const line = (yFrac, dashed) => {
        const y = Math.round(box.y + yFrac * box.s) + 0.5;
        ctx.beginPath();
        ctx.setLineDash(dashed ? [Math.max(6, box.s * 0.03), Math.max(6, box.s * 0.03)] : []);
        ctx.strokeStyle = dashed ? "#D9E1F0" : "#C6D2E8";
        ctx.lineWidth = dashed ? 2 : 2.5;
        ctx.moveTo(inset, y);
        ctx.lineTo(w - inset, y);
        ctx.stroke();
        ctx.setLineDash([]);
      };
      line(GUIDE_LINES.top, false);
      line(GUIDE_LINES.mid, true);
      line(GUIDE_LINES.base, false);

      const bandW = box.s * 0.1;
      const inkW = box.s * 0.072;
      const faint = "#E1E6EF";
      const band = mixHex(accent, "#FFFFFF", 0.7);

      // Whole glyph, faint.
      if (showGuide) s.strokes.forEach((st, i) => { if (i > s.index) strokePolyline(ctx, st.poly, box, faint, bandW); });
      // Passed strokes, solid accent.
      s.strokes.forEach((st, i) => { if (i < s.index || s.phase === "done") strokePolyline(ctx, st.poly, box, accent, bandW); });

      const cur = s.strokes[s.index];
      const active = cur && s.phase !== "done";
      if (active) {
        // Current stroke band.
        strokePolyline(ctx, cur.poly, box, s.phase === "pass" ? accent : showGuide ? band : faint, bandW);

        // Demo: trail + pencil head sweeping along the stroke.
        if (s.demo) {
          const t = s.demo.static || cur.dot ? 1 : Math.min(1, (now - s.demo.t0) / s.demo.dur);
          const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
          if (!cur.dot) strokePolyline(ctx, cur.even, box, mixHex(accent, "#FFFFFF", 0.3), bandW * 0.62, 0, eased);
          const head = cur.dot ? { ...cur.center, dx: 0.7, dy: -0.7 } : pointAlong(cur.even, eased);
          const bob = cur.dot && !reduceMotion ? Math.abs(Math.sin((now - s.demo.t0) / 180)) * box.s * 0.03 : 0;
          const hx = box.x + head.x * box.s;
          const hy = box.y + head.y * box.s - bob;
          const r = box.s * 0.05;
          ctx.save();
          ctx.translate(hx, hy);
          ctx.rotate(Math.atan2(head.dy, head.dx));
          ctx.shadowColor = "rgba(11,26,51,0.25)";
          ctx.shadowBlur = 8;
          ctx.shadowOffsetY = 2;
          ctx.beginPath();
          ctx.moveTo(r * 1.1, 0);
          ctx.lineTo(-r * 0.8, -r * 0.85);
          ctx.lineTo(-r * 0.35, 0);
          ctx.lineTo(-r * 0.8, r * 0.85);
          ctx.closePath();
          ctx.lineJoin = "round";
          ctx.lineWidth = 3;
          ctx.strokeStyle = "#FFFFFF";
          ctx.stroke();
          ctx.fillStyle = accentDeep;
          ctx.fill();
          ctx.restore();
          const hold = s.demo.static || cur.dot ? 1400 : s.demo.dur + 350;
          if (now - s.demo.t0 > hold) s.demo = null;
        }

        // End star (where to stop).
        if (s.phase !== "pass" && !cur.dot) {
          const ex = box.x + cur.end.x * box.s;
          const ey = box.y + cur.end.y * box.s;
          drawStar(ctx, ex, ey, box.s * 0.038, LEARN.color.sun, "#FFFFFF");
        }

        // Numbered start dot, pulsing.
        if (s.phase !== "pass") {
          const sx = box.x + cur.start.x * box.s;
          const sy = box.y + cur.start.y * box.s;
          const pulse = reduceMotion ? 1 : 1 + Math.sin(now / 260) * 0.09;
          const r = box.s * 0.052 * pulse;
          if (!reduceMotion) {
            ctx.beginPath();
            ctx.arc(sx, sy, r * 1.55, 0, Math.PI * 2);
            ctx.fillStyle = mixHex(accent, "#FFFFFF", 0.78);
            ctx.globalAlpha = 0.5 + Math.sin(now / 260) * 0.25;
            ctx.fill();
            ctx.globalAlpha = 1;
          }
          ctx.beginPath();
          ctx.arc(sx, sy, r, 0, Math.PI * 2);
          ctx.fillStyle = accent;
          ctx.fill();
          ctx.lineWidth = 3;
          ctx.strokeStyle = "#FFFFFF";
          ctx.stroke();
          ctx.fillStyle = "#FFFFFF";
          ctx.font = `700 ${Math.round(r * 1.15)}px ${LEARN.font}`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(String(s.index + 1), sx, sy + r * 0.05);
        }
      }

      // Earlier strokes' ink (kept, solid accent).
      s.passedInks.forEach((ink) => strokeInk(ctx, ink, box, accent, inkW));

      // Fading ink from a miss.
      if (s.fail) {
        const t = (now - s.fail.t0) / (reduceMotion ? 1 : 900);
        if (t >= 1) s.fail = null;
        else strokeInk(ctx, s.fail.ink, box, mixHex(accent, "#FFFFFF", 0.32), inkW, 1 - Math.max(0, t - 0.3) / 0.7);
      }

      // Live ink (turns solid on pass).
      if (s.ink.length) {
        const solid = s.phase === "pass" || s.phase === "done";
        strokeInk(ctx, s.ink, box, solid ? accent : mixHex(accent, "#FFFFFF", 0.32), inkW);
      }

      // Sparkles at the end point.
      if (s.sparkles.length) {
        s.sparkles = s.sparkles.filter((p) => now - p.t0 < p.life);
        s.sparkles.forEach((p) => {
          const t = (now - p.t0) / p.life;
          const x = box.x + p.x * box.s + p.vx * t * box.s;
          const y = box.y + p.y * box.s + p.vy * t * box.s + 0.12 * t * t * box.s;
          const r = p.r * box.s * (1 - t * 0.6);
          ctx.globalAlpha = 1 - t * t;
          if (p.star) drawStar(ctx, x, y, r * 1.6, p.color);
          else {
            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fillStyle = p.color;
            ctx.fill();
          }
        });
        ctx.globalAlpha = 1;
      }

      if (s.pass && now - s.pass.t0 > 900) s.pass = null;
      if (s.bounce && now - s.bounce.t0 > 700) s.bounce = null;
      ctx.restore();
    },
    [accent, accentDeep, reduceMotion, showGuide],
  );

  const drawRef = useRef(draw);
  drawRef.current = draw;

  const kick = useCallback(() => {
    if (rafRef.current || !ctxRef.current) return;
    if (typeof requestAnimationFrame !== "function") {
      drawRef.current(nowMs());
      return;
    }
    rafRef.current = requestAnimationFrame((now) => {
      rafRef.current = 0;
      drawRef.current(now);
      if (animating()) kick();
    });
  }, [animating]);

  // ---------- lifecycle ----------
  const startDemo = useCallback(
    (delay = 0) => {
      const s = m.current;
      const cur = s.strokes[s.index];
      if (!cur || !showGuide || s.phase === "done") return;
      const begin = () => {
        if (m.current.phase === "done" || m.current.drawing) return;
        m.current.demo = reduceMotion
          ? { t0: nowMs(), dur: 1, static: true }
          : { t0: nowMs(), dur: 650 + cur.length * 1300, static: false };
        kick();
      };
      if (delay > 0) after(delay, begin);
      else begin();
    },
    [after, kick, reduceMotion, showGuide],
  );

  const resetAll = useCallback(
    (opts = {}) => {
      const s = m.current;
      s.strokes = strokes;
      s.index = 0;
      s.phase = strokes.length ? "trace" : "done";
      s.drawing = null;
      s.ink = [];
      s.passedInks = [];
      s.demo = null;
      s.fail = null;
      s.pass = null;
      s.bounce = null;
      s.sparkles = [];
      setStrokeIndex(0);
      setPhase(s.phase);
      timers.current.forEach((id) => clearTimeout(id));
      timers.current.clear();
      if (!opts.keepDone) doneRef.current = false;
      if (!reduceMotion) startDemo(opts.demoDelay ?? 500);
      kick();
    },
    [kick, reduceMotion, startDemo, strokes],
  );

  useEffect(() => {
    resetAll({ demoDelay: 700 });
    // Reset whenever the glyph (or its stroke set) changes.
  }, [resetAll]);

  useEffect(() => {
    const timerSet = timers.current;
    return () => {
      timerSet.forEach((id) => clearTimeout(id));
      timerSet.clear();
      if (rafRef.current && typeof cancelAnimationFrame === "function") cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };
  }, []);

  // Canvas sizing: container size × devicePixelRatio.
  useLayoutEffect(() => {
    const paper = paperRef.current;
    const canvas = canvasRef.current;
    if (!paper || !canvas) return undefined;
    ctxRef.current = canvas.getContext ? canvas.getContext("2d") : null;
    const apply = (w, h) => {
      const dpr = Math.max(1, Math.min(3, (typeof window !== "undefined" && window.devicePixelRatio) || 1));
      const W = Math.max(1, Math.round(w));
      const H = Math.max(1, Math.round(h));
      sizeRef.current = { w: W, h: H, dpr };
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      const ctx = ctxRef.current;
      if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        drawRef.current(nowMs());
      }
      kick();
    };
    if (typeof ResizeObserver === "undefined") {
      const r = paper.getBoundingClientRect();
      apply(r.width, r.height);
      return undefined;
    }
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width && height) apply(width, height);
    });
    ro.observe(paper);
    return () => ro.disconnect();
  }, [kick]);

  // ---------- pointer → ink ----------
  const toNorm = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const box = glyphBoxFor(rect.width, rect.height);
    const px = Number(e.clientX) - rect.left;
    const py = Number(e.clientY) - rect.top;
    return { x: (px - box.x) / box.s, y: (py - box.y) / box.s, px, py };
  }, []);

  const addInk = useCallback((e, t) => {
    const s = m.current;
    const p = toNorm(e);
    if (!p) return;
    let w = s.widthFactor;
    if (s.lastPx) {
      const d = Math.hypot(p.px - s.lastPx.px, p.py - s.lastPx.py);
      if (d < 1.2 && s.ink.length) return;
      const dt = Math.max(4, t - s.lastPx.t);
      w = crayonWidthFactor(d / dt, s.widthFactor);
    }
    s.widthFactor = w;
    s.lastPx = { px: p.px, py: p.py, t };
    s.ink.push({ x: p.x, y: p.y, w, t });
  }, [toNorm]);

  const finishGlyph = useCallback(() => {
    const s = m.current;
    s.phase = "done";
    setPhase("done");
    s.bounce = reduceMotion ? null : { t0: nowMs() };
    cbRef.current.sfx?.("correct");
    kick();
    after(reduceMotion ? 0 : 560, () => {
      if (doneRef.current) return;
      doneRef.current = true;
      cbRef.current.onComplete?.();
    });
  }, [after, kick, reduceMotion]);

  const advance = useCallback(() => {
    const s = m.current;
    if (s.phase === "done") return;
    if (s.ink.length) s.passedInks.push(s.ink);
    s.ink = [];
    s.pass = null;
    if (s.index + 1 < s.strokes.length) {
      s.index += 1;
      s.phase = "trace";
      setStrokeIndex(s.index);
      setPhase("trace");
      const next = s.strokes[s.index];
      if (next?.prompt) cbRef.current.speak?.(next.prompt, { interrupt: true });
      startDemo(reduceMotion ? 0 : 250);
      kick();
    } else {
      finishGlyph();
    }
  }, [finishGlyph, kick, reduceMotion, startDemo]);

  const endAttempt = useCallback(() => {
    const s = m.current;
    if (!s.drawing) return;
    s.drawing = null;
    s.lastPx = null;
    const cur = s.strokes[s.index];
    if (!cur || s.phase !== "trace") {
      s.ink = [];
      kick();
      return;
    }
    const verdict = judgeAttempt(cur, s.ink);
    if (!verdict.attempt) {
      // A tap, not a try: clear quietly and nudge with the demo.
      s.ink = [];
      startDemo(0);
      kick();
      return;
    }
    if (verdict.passed) {
      s.phase = "pass";
      setPhase("pass");
      s.pass = { t0: nowMs() };
      if (!reduceMotion) {
        const end = cur.end;
        const colors = [accent, LEARN.color.sun, "#FFFFFF", LEARN.color.mint, LEARN.color.coral];
        s.sparkles = Array.from({ length: SPARKLE_COUNT }, (_, i) => {
          const a = (i / SPARKLE_COUNT) * Math.PI * 2 + Math.random() * 0.5;
          const v = 0.12 + Math.random() * 0.16;
          return {
            x: end.x, y: end.y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 0.06,
            r: 0.012 + Math.random() * 0.014, color: colors[i % colors.length], star: i % 4 === 0,
            t0: nowMs(), life: 620 + Math.random() * 300,
          };
        });
      }
      cbRef.current.sfx?.("sparkle");
      cbRef.current.onStrokeDone?.(s.index);
      kick();
      after(reduceMotion ? 0 : 620, advance);
      return;
    }
    // Miss: shake, fade the ink, replay the demo and the prompt.
    s.fail = { t0: nowMs(), ink: s.ink };
    s.ink = [];
    cbRef.current.onWrong?.();
    kick();
    after(reduceMotion ? 0 : 520, () => {
      if (cur.prompt) cbRef.current.speak?.(cur.prompt, { interrupt: true });
      startDemo(0);
    });
  }, [accent, advance, after, kick, reduceMotion, startDemo]);

  const onPointerDown = useCallback(
    (e) => {
      const s = m.current;
      if (s.phase !== "trace") return;
      if (e.button != null && e.button > 0) return;
      const t = nowMs();
      if (e.pointerType === "pen") s.penSeen = t;
      // Palm rejection: ignore touches while a pen has been active recently.
      if (e.pointerType === "touch" && t - s.penSeen < 1500) return;
      if (s.drawing) return; // one finger at a time
      e.preventDefault?.();
      try {
        e.currentTarget.setPointerCapture?.(e.pointerId);
      } catch {
        /* synthetic pointer ids can't be captured; tracing still works */
      }
      s.drawing = { pointerId: e.pointerId };
      s.demo = null;
      s.fail = null;
      s.ink = [];
      s.widthFactor = 1;
      s.lastPx = null;
      addInk(e, t);
      kick();
    },
    [addInk, kick],
  );

  const onPointerMove = useCallback(
    (e) => {
      const s = m.current;
      if (!s.drawing || s.drawing.pointerId !== e.pointerId) return;
      const events = typeof e.nativeEvent?.getCoalescedEvents === "function" ? e.nativeEvent.getCoalescedEvents() : null;
      const t = nowMs();
      if (events && events.length) events.forEach((ce, i) => addInk(ce, t - (events.length - 1 - i) * 4));
      else addInk(e, t);
      kick();
    },
    [addInk, kick],
  );

  const onPointerUp = useCallback(
    (e) => {
      const s = m.current;
      if (!s.drawing || s.drawing.pointerId !== e.pointerId) return;
      addInk(e, nowMs());
      endAttempt();
    },
    [addInk, endAttempt],
  );

  const onPointerCancel = useCallback(
    (e) => {
      const s = m.current;
      if (!s.drawing || s.drawing.pointerId !== e.pointerId) return;
      endAttempt();
    },
    [endAttempt],
  );

  const showMe = () => {
    const s = m.current;
    if (s.phase !== "trace" || s.drawing) return;
    cbRef.current.sfx?.("pop", { volume: 0.4 });
    const cur = s.strokes[s.index];
    if (cur?.prompt) cbRef.current.speak?.(cur.prompt, { interrupt: true });
    s.demo = reduceMotion
      ? { t0: nowMs(), dur: 1, static: true }
      : { t0: nowMs(), dur: 650 + (cur?.length || 0.5) * 1300, static: false };
    kick();
  };

  const startOver = () => {
    if (m.current.phase === "done") return;
    cbRef.current.sfx?.("pop", { volume: 0.4 });
    resetAll({ keepDone: true, demoDelay: 150 });
    const first = strokes[0];
    if (first?.prompt) cbRef.current.speak?.(first.prompt, { interrupt: true });
  };

  const name = label || `Trace ${describeGlyph(glyph)}`;
  const ariaLabel = total
    ? phase === "done"
      ? `${name}: finished`
      : `${name}: stroke ${strokeIndex + 1} of ${total}, ${current?.prompt || ""}`
    : `${name}: no strokes available`;

  return (
    <div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex", flexDirection: "column", fontFamily: LEARN.font, ...style }}>
      <div
        ref={paperRef}
        style={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          borderRadius: LEARN.radius.lg,
          boxShadow: LEARN.shadow.card,
          border: `2px solid ${LEARN.color.line}`,
          overflow: "hidden",
        }}
      >
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={ariaLabel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onLostPointerCapture={onPointerCancel}
          onContextMenu={(e) => e.preventDefault()}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            touchAction: "none",
            cursor: phase === "trace" ? "crosshair" : "default",
            WebkitUserSelect: "none",
            userSelect: "none",
            WebkitTouchCallout: "none",
            display: "block",
          }}
        />
      </div>
      <div style={{ flexShrink: 0, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, padding: "8px 2px 0" }}>
        <button
          type="button"
          onClick={startOver}
          disabled={phase === "done"}
          style={{
            fontFamily: LEARN.font, fontWeight: 700, fontSize: 14, color: LEARN.color.muted,
            background: "rgba(255,255,255,0.75)", border: `2px solid ${LEARN.color.line}`, borderRadius: LEARN.radius.pill,
            padding: "8px 14px", minHeight: 40, cursor: phase === "done" ? "default" : "pointer",
            display: "inline-flex", alignItems: "center", gap: 6, opacity: phase === "done" ? 0.5 : 1,
          }}
        >
          <RestartIcon />
          Start over
        </button>
        <button
          type="button"
          onClick={showMe}
          disabled={phase !== "trace"}
          aria-label="Show me how to trace this stroke"
          style={{
            ...stickerButton({ bg: accent, deep: accentDeep, size: "sm" }),
            minHeight: 40, display: "inline-flex", alignItems: "center", gap: 6,
            opacity: phase !== "trace" ? 0.6 : 1, cursor: phase !== "trace" ? "default" : "pointer",
          }}
        >
          <ShowMeIcon />
          Show me
        </button>
      </div>
    </div>
  );
}
